import { useAuthState, useClient, useQuery } from '@based/sdk/react'
import {
  useBasedEndpoints,
  useBasedSchema,
} from '../providers/Endpoints/index.js'
import { useToast } from '../components/Toast/index.js'
import { capitalize } from '../utils/text.js'
import { useEffect, useState } from 'react'

export const useFollowMe = (): [
  Record<string, any>,
  (patch: Partial<Record<string, any>>) => void,
] => {
  const client = useClient()
  const endpoints = useBasedEndpoints()
  const { token } = useAuthState()
  const toast = useToast()
  const [followMeEnabled, setFollowMeEnabled] = useState(false)
  const { data: schemaData, checksum: schemaChecksum } = useBasedSchema()

  useEffect(() => {
    if (!schemaData) {
      return
    }

    setFollowMeEnabled(
      // @ts-ignore
      schemaData?.types?.user?.props?.followMe !== undefined,
    )
  }, [schemaChecksum])

  const { data: { me, all } = {} } = useQuery(
    token ? endpoints.users : '',
    { token },
    { persistent: true },
  )

  const addFollower = (arr: number[] = [], value: number): number[] => {
    const set = new Set(arr)
    set.add(value)
    return [...set]
  }

  const removeFollower = (arr: number[] = [], value: number): number[] => {
    const set = new Set(arr)
    set.delete(value)
    return [...set]
  }

  const getUser = (userId: number) => all?.find(({ id }) => id === userId)

  const makeFollowMe = (patch: Partial<Record<string, any>>) => {
    if (me?.followMe) {
      return Object.assign(me?.followMe, patch)
    }
  }

  const addMeAsAFollower = async (
    whoIWantToFollow: Partial<Record<string, any>>,
  ) => {
    return await applyFollowMe(whoIWantToFollow?.id, {
      ...whoIWantToFollow?.followMe,
      followers: addFollower(whoIWantToFollow?.followMe?.followers, me.id),
    })
  }

  const removeMeAsAFollower = async (allUsers: any[]) => {
    const whoIFollow = allUsers.filter(({ followMe }) =>
      followMe?.followers?.includes(me.id),
    )

    await Promise.all(
      whoIFollow.map(
        async (user) =>
          await applyFollowMe(user?.id, {
            ...user?.followMe,
            followers: removeFollower(user?.followMe?.followers, me.id),
          }),
      ),
    )
  }

  const removeMyFollowers = async (myFollowers: any[]) => {
    if (!Array.isArray(myFollowers)) {
      return
    }

    await Promise.all(
      myFollowers.map(async (user) => {
        user = getUser(user)

        if (!user) {
          return false
        }

        return await applyFollowMe(user?.id, {
          ...user?.followMe,
          following: null,
        })
      }),
    )

    await applyFollowMe(
      me.id,
      makeFollowMe({
        followers: [],
      }),
    )
  }

  const applyMyLocationToMyFollowers = async (myFollowers: any[]) => {
    if (!Array.isArray(myFollowers) || !myFollowers?.length) {
      return
    }

    for (let user of myFollowers) {
      user = getUser(user)

      if (!user) {
        continue
      }

      await applyFollowMe(user?.id, {
        ...user?.followMe,
        following: {
          ...user?.followMe?.following,
          location: me?.followMe?.location,
        },
      })
    }
  }

  const setFollowing = async (id: number, color: string, location: string) => {
    return await applyFollowMe(
      me.id,
      makeFollowMe({
        following: {
          id,
          location,
          color,
        },
        followers: [],
      }),
    )
  }

  const applyFollowMe = async (
    id: number = me.id,
    followMe: Partial<Record<string, any>> = {},
  ) => {
    return await client
      .call(endpoints.update, {
        id,
        type: 'user',
        followMe,
      })
      .catch(console.error)
  }

  const setFollowMe = async (patch: Partial<Record<string, any>>) => {
    if (!me?.id || !followMeEnabled) {
      return
    }

    const { location, privateMode, following, followers, ...rest } = patch

    // Toggle Private Mode
    if (privateMode !== undefined) {
      // Remove all my followers (same as 'If I want to stop being followed')
      await removeMyFollowers(me?.followMe?.followers)

      // Search me as a follower and remove me from the followers list of the users
      await removeMeAsAFollower(all)

      // Set Private Mode
      await applyFollowMe(me.id, makeFollowMe({ privateMode }))

      // Set following
      return await setFollowing(null, null, null)
    }

    // Set Location
    if (location !== undefined) {
      await applyFollowMe(me.id, makeFollowMe({ location }))

      // Search for followers and change their following location
      await applyMyLocationToMyFollowers(me?.followMe?.followers || [])

      // Taking back the control and stop following location
      if (
        me?.followMe?.following?.id !== undefined &&
        me?.followMe?.location !== me?.followMe?.following?.location &&
        (me?.followMe?.followers === undefined ||
          !me?.followMe?.followers?.length)
      ) {
        await removeMeAsAFollower(all)
        await setFollowing(null, null, null)
      }

      return
    }

    // If I want to stop being followed
    if (Array.isArray(followers) && followers?.length === 0) {
      return await removeMyFollowers(me?.followMe?.followers)
    }

    // Follow & Unfollow a User
    if (following?.id && following?.color) {
      const whoIWantToFollow = getUser(following?.id)

      if (!whoIWantToFollow) {
        return
      }

      // I cannot follow someone that is currently following someone or is set to private session
      if (
        whoIWantToFollow?.followMe?.following?.id ||
        whoIWantToFollow?.followMe?.privateMode
      ) {
        return toast(`Couldn't follow ${capitalize(whoIWantToFollow.name)}`, {
          description: `${capitalize(whoIWantToFollow.name)} is following someone else`,
          icon: 'error-filled',
          iconColor: 'white100',
          color: 'red',
        })
      }

      // Remove all my followers (same as 'If I want to stop being followed')
      await removeMyFollowers(me?.followMe?.followers)

      // Add me in the followers list of the user
      await addMeAsAFollower(whoIWantToFollow)

      // Search me as a follower and remove me from the followers list of the users
      await removeMeAsAFollower(all)

      if (me?.followMe?.following?.id === following?.id) {
        // If is the same user then unfollow
        return await setFollowing(
          null,
          null,
          whoIWantToFollow?.followMe?.location,
        )
      } else {
        // Set following
        return await setFollowing(
          following?.id,
          following?.color,
          whoIWantToFollow?.followMe?.location,
        )
      }
    } else if (following?.id === null && following?.color === null) {
      // Search me as a follower and remove me from the followers list of the users
      await removeMeAsAFollower(all)

      // Set following
      return await setFollowing(null, null, null)
    }

    // Adding new data to followMe
    if (rest) {
      return await applyFollowMe(me.id, makeFollowMe(rest))
    }
  }

  useEffect(() => {
    if (me?.id !== undefined && me?.followMe === null) {
      applyFollowMe(me.id, {})
    }
  }, [!me?.followMe])

  const {
    id,
    name,
    email,
    followMe: { location = '', finder = {} } = {},
  } = getUser(me?.followMe?.following?.id) || {}

  const followMe = {
    ...me?.followMe,
    following: {
      ...me?.followMe?.following,
      id,
      name,
      email,
      location,
      finder,
    },
  }

  return [followMe || {}, setFollowMe]
}
